import { useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Button,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import IconEdit from "@tabler/icons-react/dist/esm/icons/IconEdit.mjs";
import IconX from "@tabler/icons-react/dist/esm/icons/IconX.mjs";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { ReactMultiEmail } from "react-multi-email";
import { editRuleStatusCustomReports } from "../../../../../services/rules";
import { getAxiosResponseError } from "../../../../../utils/functions";
import {
  updateReport,
  useSelectReportsData,
} from "../../../../../redux/features/customReport/customReportsSlice";
import "./EditReportPopup.scss";

const periods = [
  { value: "DAY", label: "Day" },
  { value: "WEEK", label: "Week" },
  { value: "MONTH", label: "Month" },
  { value: "QUARTER", label: "Quarter" },
  { value: "YEAR", label: "Year" },
];

const initialForm = {
  id: null,
  date: dayjs(new Date()),
  period: "",
  periodCount: "",
  name: "",
  emailsOfReceivers: [],
};

export default function EditReportModal({ reportId }) {
  const dispatch = useDispatch();
  const reports = useSelectReportsData();
  const [opened, { open, close }] = useDisclosure(false);
  const report = useMemo(
    () => reports.find((item) => item.id === reportId),
    [reportId, reports],
  );
  const [form, setForm] = useState(initialForm);

  useEffect(() => {
    if (!opened || !report) {
      return;
    }

    setForm({
      ruleId: report.id,
      date: dayjs(new Date()),
      period: report.period || "",
      periodCount: report.periodCount || "",
      name: report.name || "",
      emailsOfReceivers: report.emailsOfReceivers?.split(",") || [],
    });
  }, [opened, report]);

  const isValid = Boolean(form.period?.length && form.name?.length);

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {
        ...form,
        emailsOfReceivers: form.emailsOfReceivers.join(","),
      };

      await editRuleStatusCustomReports(payload);
      return payload;
    },
    onSuccess: (payload) => {
      dispatch(
        updateReport({
          id: payload.ruleId,
          name: payload.name,
          period: payload.period,
          periodCount: payload.periodCount,
          emailsOfReceivers: payload.emailsOfReceivers,
        }),
      );

      toast.success("Report has been updated successfully");
      handleClose();
    },
    onError: (err) => {
      console.error(err);
      toast.error(getAxiosResponseError(err));
    },
  });

  const handleClose = () => {
    setForm(initialForm);
    close();
  };

  const updateForm = (key, value) => {
    setForm((current) => ({
      ...current,
      [key]: value,
    }));
  };

  return (
    <>
      <ActionIcon c="gray" variant="transparent" onClick={open}>
        <IconEdit />
      </ActionIcon>

      <Modal
        opened={opened}
        onClose={handleClose}
        title="Edit report"
        centered
        className="edit-report-modal"
      >
        <Stack gap="md">
          <Group grow align="flex-start">
            <TextInput
              label="Name"
              value={form.name}
              onChange={(event) =>
                updateForm("name", event.currentTarget.value)
              }
            />

            <Select
              label="Period"
              placeholder="Select period"
              data={periods}
              value={form.period}
              onChange={(value) => updateForm("period", value)}
            />

            <NumberInput
              label="Count"
              min={1}
              value={form.periodCount}
              onChange={(value) => updateForm("periodCount", value)}
            />
          </Group>

          <Stack gap={6}>
            <Text size="sm" fw={500}>
              Emails of receivers
            </Text>
            <ReactMultiEmail
              placeholder="Emails"
              emails={form.emailsOfReceivers}
              onChange={(emails) => updateForm("emailsOfReceivers", emails)}
              getLabel={(email, index, removeEmail) => (
                <div data-tag key={index}>
                  <div data-tag-item>{email}</div>
                  <button
                    type="button"
                    data-tag-handle
                    onClick={() => removeEmail(index)}
                  >
                    <IconX size={12} />
                  </button>
                </div>
              )}
            />
          </Stack>

          <Group justify="space-between">
            <Button variant="outline" color="red" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="outline"
              color="green"
              disabled={!isValid}
              loading={mutation.isPending}
              onClick={() => mutation.mutate()}
            >
              Confirm
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
